import { Component, Inject, OnInit, PLATFORM_ID, AfterViewInit, ViewChild } from '@angular/core';
import { IconResolver, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet, ActivatedRoute } from '@angular/router';
import { AppService } from './services/app.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { SessionStorageService } from './services/session-storage.service';
import { filter } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import * as langConfig from './../assets/languageConfig/languageConfig.json';
import { isPlatformBrowser } from '@angular/common';
import { SettingsService } from './services/settings.service';
import { AppointmentDetails } from './services/client.service';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
  ],
  providers: [],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  })
export class AppComponent implements OnInit, AfterViewInit {
  loader: boolean = false;
  showIconContainer: boolean = false;
  showHomeIcon: boolean = false;
  headerReady: boolean = false;
  private hasTokenOnInit: boolean = false;
  hideHeaderWhenCancelRoute: boolean = false;
  languageConfiguration: any = langConfig;
  currentLanguage: any = 'EN';
  disableLoader:boolean =false;
  errorMessage: string = $localize`Online Appointment Booking not supported. Please contact dealer directly.`;
  isDealerComponentReady: boolean = false;
  translateIconLoaded: boolean = false;
  
  @ViewChild('language') languageIcon!: MatIcon;

  constructor(
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private readonly appService: AppService,
    private readonly sessionStorageService: SessionStorageService,
    public readonly router: Router,
    private readonly route: ActivatedRoute,
    public dialog: MatDialog,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly settingsService: SettingsService
  ) {

    if (isPlatformBrowser(this.platformId)) {
      this.initializeLanguageBeforeRender();
      this.changeDisplayLanguageCode();
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = window.location.pathname;
        let lang = url.split('/')[1];

        if (!lang) {
          lang = 'en-US';
        }

        if (localStorage.getItem('lang')?.toString() !== lang) {
          this.changeLanguage(lang);
        }

        this.disableLoader = event.urlAfterRedirects.includes('/getAppointment') || event.urlAfterRedirects.includes('/cancel');
      }
    });

    const resolver: IconResolver = (name) =>
      sanitizer.bypassSecurityTrustResourceUrl(`svg/${name}.svg`);
    iconRegistry.addSvgIconResolver(resolver);
  }

  ngOnInit(): void {
    this.appService.componentReady$.subscribe((ready: boolean) => {
      this.isDealerComponentReady = ready;
    });
    
    if (isPlatformBrowser(this.platformId)) {
      this.disableLoader = this.shouldDisableLoaderByUrl(window.location.pathname);
    }
    this.appService.loader$?.subscribe((data: boolean) => {
      setTimeout(() => {
        this.loader = data && !this.disableLoader;
      }, 0);
    });
    this.route.queryParams.subscribe((params) => {
      const token = params['token'];
      if (token) {
        this.hasTokenOnInit = true;
        const decodedToken = decodeURIComponent(token);
        const cleanToken = decodedToken.endsWith('.') ? decodedToken.slice(0, -1) : decodedToken;
        this.sessionStorageService.setSessionItem('justLoggedIn', 'true');

        const originalPath = globalThis.location.pathname;
        this.router.navigate(['/splash'], { 
          queryParams: { 
            originalPath: originalPath 
          },
          replaceUrl: true 
        });

        this.handleTokenLogin(cleanToken);
      }
    });
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const currentUrl = event.url;
        const urlForMatch = (event.urlAfterRedirects || event.url).split('?')[0].split('#')[0];
        this.disableLoader = this.shouldDisableLoaderByUrl(urlForMatch);
        if (currentUrl.includes('dealer')) {
          this.showIconContainer = false;
          this.showHomeIcon = false;
        } else if (currentUrl.includes('locations')) {
          this.showIconContainer = true;
          this.showHomeIcon = false;
        } else if (currentUrl.includes('confirmation')) {
          this.showIconContainer = true;
          this.showHomeIcon = false;
        }
        else if (currentUrl.includes('cancel')) {
          this.showIconContainer = true;
          this.showHomeIcon = false;
        }
        else if (currentUrl.includes('getAppointment')) {
          this.showIconContainer = true;
          this.showHomeIcon = false;
        } else {
          this.showIconContainer = true;
          this.showHomeIcon = !currentUrl.includes('home');
        }
        if (this.hasTokenOnInit) {
          if (
            currentUrl.includes('locations') ||
            currentUrl.includes('getAppointment') ||
            currentUrl.includes('dealer') ||
            currentUrl.includes('confirmation')
          ) {
            this.headerReady = true;
            this.hasTokenOnInit = false;
          } else {
            this.headerReady = false;
          }
        } else {
          this.headerReady = true;
        }
        if (isPlatformBrowser(this.platformId)) {
          setTimeout(() => {
            const scrollingContainer = document.scrollingElement || document.documentElement;
            if (scrollingContainer) {
              (scrollingContainer as any).scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
              (scrollingContainer as any).scrollTop = 0;
            }
            document.body.scrollTop = 0;
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

            ['.content', '.stepper'].forEach((sel) => {
              const node = document.querySelector(sel) as HTMLElement | null;
              if (node && typeof (node as any).scrollTo === 'function') {
                (node as any).scrollTo({ top: 0, left: 0, behavior: 'auto' });
              } else if (node) {
                node.scrollTop = 0;
                node.scrollLeft = 0;
              }
            });
          }, 0);
        }
      });
    if (isPlatformBrowser(this.platformId)) {
      this.setDefaultLanguage();
    }
  }

  private initializeLanguageBeforeRender(): void {
    const storedLang = window.localStorage.getItem('lang');
    const pathSegments = window.location.pathname.split('/').filter((seg) => seg);
    const urlLang = pathSegments[0];
    const supportedLanguages = [
      'en-US',
      'de-DE',
      'es-MX',
      'es-ES',
      'pt-PT',
      'zh-CN',
      'it-IT',
      'es-LATAM',
    ];

    if (storedLang && supportedLanguages.includes(storedLang)) {
      return;
    }
    if (supportedLanguages.includes(urlLang)) {
      window.localStorage.setItem('lang', urlLang);
      return;
    }
    window.localStorage.setItem('lang', 'en-US');
  }

  private handleTokenLogin(token: string): void {
    this.appService.setLoading(true);

    this.appService.loginWithToken(token).subscribe({
      next: (response: any) => {
        if (!response) {
          this.appService.handleErrorMessage('No response received from login API');
          return;
        }

        const sessionToken =
          response.Session?.Token ||
          response.session?.token ||
          response.Session?.token;

        if (sessionToken) {
          this.sessionStorageService.setSessionItem('oab-profile', sessionToken);
        } else {
          this.appService.handleErrorMessage(
            'No session token found in response, storing original token as fallback'
          );
          this.sessionStorageService.setSessionItem('oab-profile', token);
        }

        const appointmentId =
          response.Session?.AppointmentId ||
          response.session?.appointmentId ||
          response.appointmentId;

        const companiesData =
          response.Companies || response.companies || response.company;

        if (!companiesData) {
          this.appService.handleErrorMessage(this.errorMessage);
          return;
        }

        const companiesArray = Array.isArray(companiesData)
          ? companiesData
          : [companiesData];

        companiesArray.forEach((company: any, companyIndex: number) => {
          company.isSelected = companyIndex === 0;
          const locations = company.Locations || company.locations;
          if (locations && locations.length > 0) {
            locations.forEach((location: any, locationIndex: number) => {
              location.isSelected =
                companyIndex === 0 && locationIndex === 0;
            });
          }
        });

        this.sessionStorageService.setSessionItem(
          'companiesList',
          JSON.stringify(companiesArray)
        );

        if (appointmentId) {
          this.sessionStorageService.setSessionItem('appointmentId', appointmentId);
        }
      },
      error: (error) => {
        this.appService.handleErrorMessage(error);
        this.appService.setLoading(false);
      },
      complete: () => {
        this.appService.setLoading(false);
      },
    });
  }

  logout() {
    this.sessionStorageService.removeSessionItem('oab-profile');
    this.router.navigate(['/dealer']);
  }

  onHomeIconClick() {
    this.sessionStorageService.setSessionItem('showMapView', 'false');
    this.sessionStorageService.removeSessionItem('oabAppSessionData');
    this.router.navigate(['/locations']);
  }

  private fetchAppointmentDetails(appointmentId: string): void {
    this.appService.getAppointment(appointmentId).subscribe({
      next: (appointmentData: AppointmentDetails) => {
        this.sessionStorageService.setSessionItem(
          'appointmentData',
          JSON.stringify(appointmentData)
        );
        this.router.navigate(['/getAppointment'], { replaceUrl: true });
      },
      error: (error) => {
        this.appService.handleErrorMessage(error);
      },
    });
  }

  changeLanguage(lang: string) {
    this.switchLanguage(lang);
    this.changeDisplayLanguageCode();
  }

  switchLanguage(lang: string) {
    window.localStorage.setItem('lang', lang);
    if (!window.location.href.includes("localhost:")) {
      const currentUrl = new URL(window.location.href);
      let pathSegments = currentUrl.pathname.split('/').filter((seg) => seg);
      const supportedLanguages = [
        'en-US',
        'de-DE',
        'es-MX',
        'es-ES',
        'pt-PT',
        'zh-CN',
        'it-IT',
        'es-LATAM',
      ];
      if (supportedLanguages.includes(pathSegments[0])) {
        pathSegments[0] = lang;
      } else {
        pathSegments.unshift(lang);
      }

      const queryParams: Record<string, string> = {};
      currentUrl.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      const newUrl = `${currentUrl.origin}/${pathSegments.join('/')}${currentUrl.search}`;
      window.location.replace(newUrl);
      return;
    }
  }

  setDefaultLanguage() {
    this.appService.setDefaultLanguage();
    this.changeDisplayLanguageCode();
    const pathSegments = window.location.pathname
      .split('/')
      .filter((seg) => seg);
    let currentLanguage = pathSegments[0];
    const supportedLanguages = [
      'en-US', 'de-DE', 'es-ES', 'pt-PT', 'zh-CN', 'it-IT', 'es-LATAM'
    ];
    
    if (supportedLanguages.includes(currentLanguage)) {
      const storedLang = window.localStorage.getItem('lang');
      if (storedLang !== currentLanguage) {
        window.localStorage.setItem('lang', currentLanguage);
        this.changeDisplayLanguageCode();
      }
    } else {
      let currentlang = this.CurrentLanguage();
      if (currentlang !== null && supportedLanguages.includes(currentlang)) {
        this.changeDisplayLanguageCode();
        this.switchLanguage(currentlang);
      } else {
        this.switchLanguage('en-US');
      }
    }
  }

  changeDisplayLanguageCode() {
    if (isPlatformBrowser(this.platformId)) {
      let currentLanguage = localStorage.getItem('lang');
      if (currentLanguage) {
        for (let i of this.languageConfiguration.Langauges) {
          if (i.LanguageFileName == currentLanguage) {
            this.currentLanguage = i.LanguageCodeToDisplay;
            break;
          }
        }
      } else {
        this.currentLanguage = 'EN';
      }
    }
  }

  CurrentLanguage() {
    if (isPlatformBrowser(this.platformId)) {
      return window.localStorage.getItem('lang');
    }
    return null;
  }

  private shouldDisableLoaderByUrl(url: string): boolean {
    const cleanUrl = url.split('?')[0].split('#')[0];
    return (
      cleanUrl.includes('/getAppointment') ||
      cleanUrl.includes('/cancel')
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.checkIconFontLoaded();
    }
  }

  private checkIconFontLoaded(): void {
    const checkFont = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return false;
      
      const text = 'translate';
      context.font = '24px Material Icons';
      const width1 = context.measureText(text).width;
      
      context.font = '24px Arial';
      const width2 = context.measureText(text).width;
      
      return width1 !== width2;
    };

    const checkMaterialIcons = () => {
      const testElement = document.createElement('span');
      testElement.style.fontFamily = 'Material Icons';
      testElement.style.fontSize = '24px';
      testElement.style.visibility = 'hidden';
      testElement.style.position = 'absolute';
      testElement.textContent = 'translate';
      document.body.appendChild(testElement);
      
      const width = testElement.offsetWidth;
      document.body.removeChild(testElement);
      
      return width > 0;
    };

    const checkIcon = () => {
      if (checkFont() || checkMaterialIcons()) {
        this.translateIconLoaded = true;
      } else {
        setTimeout(checkIcon, 100);
      }
    };
    setTimeout(checkIcon, 100);
  }

}
